/**
 * T076: Storage Provider Selector Component
 * Dropdown component to select storage provider in cluster forms
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem, TextField, CircularProgress, FormHelperText } from '@material-ui/core';
import { getStorageProviders } from 'app/store/actions';

/**
 * StorageProviderSelector - Select storage provider for cluster
 * 
 * @param {Object} props
 * @param {string} props.value - Selected provider ID
 * @param {Function} props.onChange - Callback when selection changes (providerId, providerObj)
 * @param {boolean} props.disabled - Disable selector
 * @param {string} props.cloudProvider - Filter by cloud provider (e.g., 'ibm')
 * @param {string} props.label - Input label
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.required - Mark as required
 * @param {string} props.error - Error message
 */
const StorageProviderSelector = ({
	value = '',
	onChange,
	disabled = false,
	cloudProvider = null,
	label = 'Storage Provider',
	helperText = '',
	required = false,
	error = ''
}) => {
	const dispatch = useDispatch();
	
	const providers = useSelector(({ storage }) => storage.providers || []);
	const loading = useSelector(({ storage }) => storage.providerLoading || false);
	const storeError = useSelector(({ storage }) => storage.providerError);
	
	// Load providers on mount
	useEffect(() => {
		if (providers.length === 0) {
			dispatch(getStorageProviders());
		}
	}, [dispatch, providers.length]);
	
	// Filter providers by cloud provider and enabled status
	const filteredProviders = React.useMemo(() => {
		return providers.filter(provider => {
			// Only show enabled providers
			if (!provider.enabled) return false;

			if (!cloudProvider) return true;

			// Volumez has its own dedicated media-node configuration UI
			// (StorageConfigurationSection) - never offer it here too.
			if (provider.type === 'volumez') return false;

			// simplyblock's SPDK data plane needs real local NVMe, only
			// available on AWS instance types today, and (unlike the
			// heidi-ai/IBM type) doesn't set provider.cloudProvider at all.
			if (provider.type === 'simplyblock') return cloudProvider === 'aws';

			return provider.cloudProvider === cloudProvider;
		});
	}, [providers, cloudProvider]);
	
	const handleChange = (event) => {
		const providerId = event.target.value;
		const providerObj = providers.find(p => p._id === providerId);
		
		if (onChange) {
			onChange(providerId, providerObj);
		}
	};
	
	const selectedProvider = providers.find(p => p._id === value);
	
	return (
		<TextField
			select
			fullWidth
			label={label}
			value={value}
			onChange={handleChange}
			disabled={disabled || loading}
			required={required}
			error={Boolean(error)}
			helperText={error || helperText || (storeError ? `Error: ${storeError}` : '')}
			InputProps={{
				endAdornment: loading ? <CircularProgress size={20} /> : null
			}}
		>
			<MenuItem value="">
				<em>None</em>
			</MenuItem>
			
			{filteredProviders.length === 0 && !loading && (
				<MenuItem disabled>
					<em>
						{cloudProvider 
							? `No ${cloudProvider.toUpperCase()} storage providers available`
							: 'No storage providers available'
						}
					</em>
				</MenuItem>
			)}
			
			{filteredProviders.map((provider) => (
				<MenuItem key={provider._id} value={provider._id}>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<span style={{ fontWeight: 500 }}>
							{provider.name}
						</span>
						<span style={{ fontSize: '0.75rem', color: '#666' }}>
							{provider.cloudProvider.toUpperCase()}
							{provider.ibmConfig?.defaultZone && ` • ${provider.ibmConfig.defaultZone}`}
						</span>
					</div>
				</MenuItem>
			))}
		</TextField>
	);
};

export default StorageProviderSelector;
